// CSATOLMÁNY-SZABÁLYOK + a háromlépéses presigned feltöltő-folyamat — feature-független réteg.
//
// MIÉRT `lib/` (és nem egy feature alatt): HÁROM hely tölt fel csatolmányt — a ticket-részletező, a
// ticket-nyitó form ÉS az üzenet-szerkesztő. A szabály-készlet (kiterjesztés-whitelist, per-fájl méret,
// darabszám-plafon) mindhárom helyen UGYANAZ, a folyamat lépései is; ami eltér, az csak a KÉT végpont-hívás.
// A repo DRY-szabálya szerint (CLAUDE.md 4. főszabály) ez a közös rétegbe tartozik, nem másolódik.
//
// A TOAST-ok SZÁNDÉKOSAN nem itt vannak: a hívó dönti el, mit mond a felhasználónak (a nyitó-formon pl.
// „a jegy megnyílt, de N csatolmány nem ment fel"). Ez a modul csak a hiba FAJTÁJÁT adja vissza.
//
// A kliens-oldali validáció CSAK UX: a végső, megkerülhetetlen fal a BFF-kapu + az Odin presigned-aláírása
// (oda van kötve a méret és a típus). Ezért a lista itt a szerver whitelistjének TÜKRE, nem a forrása.

/** Engedélyezett kiterjesztések (a BFF `common/utils/attachment-guard.ts` whitelistjének tükre). */
export const ALLOWED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'svg',
  'png',
  'gif',
  'webp',
  'webm',
  'mp4',
  'mov',
  'avi',
  'wmv',
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'csv',
  'txt',
  'html',
  'xml',
  'zip',
];

/** A böngésző fájlválasztó `accept`-je (a whitelist pont-előtaggal). */
export const ATTACHMENT_ACCEPT = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');

/** Per-fájl méret-plafon: 50 MiB — az Odin fájl-tárának plafonja (a BFF `attachment-guard.ts`-ével azonos). */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** Max. csatolmány / hordozó (ticket VAGY üzenet) — a BFF `MAX_ATTACHMENTS_PER_CARRIER` tükre. */
export const MAX_ATTACHMENTS = 10;

/** A fájlnév kiterjesztése (kisbetűsen); üres, ha nincs. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

/** A kliens-oldali (UX-) validáció eredménye — a szerver a forrás-igazság. */
export type AttachmentValidation = 'ok' | 'limit' | 'type' | 'size';

/** A kiválasztott fájlok ellenőrzése: darabszám-plafon, kiterjesztés-whitelist, per-fájl méret. */
export function validateAttachments(files: File[], existingCount: number): AttachmentValidation {
  if (existingCount + files.length > MAX_ATTACHMENTS) return 'limit';
  for (const file of files) {
    if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) return 'type';
    if (file.size > MAX_ATTACHMENT_BYTES) return 'size';
  }
  return 'ok';
}

/** Egy feltöltés kimenete: siker, vagy a hiba FAJTÁJA (a hívó fordítja — nyers üzenet nem megy a felhasználóhoz). */
export type AttachmentUploadOutcome = 'ok' | 'validation' | 'forbidden' | 'failed' | 'unavailable';

/** Az `upload-url` hívás eredménye a hívótól (feature-specifikus végpont, közös alak). */
export interface PresignedTarget {
  uploadUrl: string;
  fileId: string;
}

/**
 * A HÁROMLÉPÉSES feltöltés közös motorja (a bájtok SOSEM mennek át a BFF-en):
 *   1) a hívó `requestUrl`-je → presigned PUT-URL (a típus + méret az aláírásba kötve),
 *   2) `PUT` a nyers bájtokkal KÖZVETLENÜL a tárba,
 *   3) a hívó `commit`-ja → a létrejött sor.
 * SOHA nem dob: minden hiba a kimenet-fajtára képződik. A presigned URL SOHA nem logolt.
 */
export async function runPresignedUpload(
  file: File,
  steps: {
    requestUrl: (input: {
      filename: string;
      contentType: string;
      sizeBytes: number;
    }) => Promise<{ status: number; body: unknown }>;
    commit: (input: { fileId: string; filename: string }) => Promise<{ status: number }>;
  },
): Promise<AttachmentUploadOutcome> {
  const contentType = file.type || 'application/octet-stream';
  try {
    const urlRes = await steps.requestUrl({
      filename: file.name,
      contentType,
      sizeBytes: file.size,
    });
    if (urlRes.status !== 200) return outcomeOf(urlRes.status);
    const target = urlRes.body as PresignedTarget;
    if (typeof target?.uploadUrl !== 'string' || typeof target?.fileId !== 'string') {
      return 'failed';
    }
    // A nyers bájtok KÖZVETLENÜL a tárba. A Content-Type az aláírással egyező (különben a tár elutasítja).
    const put = await fetch(target.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!put.ok) return 'failed';
    const commit = await steps.commit({ fileId: target.fileId, filename: file.name });
    return commit.status === 201 ? 'ok' : outcomeOf(commit.status);
  } catch {
    return 'failed';
  }
}

/** Státusz → kimenet-fajta (a `503` a „nincs konfigurálva" eset: a hívó ezt külön üzenettel mondja el). */
function outcomeOf(status: number): AttachmentUploadOutcome {
  if (status === 422) return 'validation';
  if (status === 403) return 'forbidden';
  if (status === 503) return 'unavailable';
  return 'failed';
}
